"use client";

import { useState, useSyncExternalStore } from "react";
import { CheckCircle2, XCircle, RotateCcw, Award, Sparkles, Trophy } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { QuizQuestion } from "@/lib/academy/quizzes";
import { saveAcademyQuizResult, getAcademyQuizResult, type AcademyQuizResult } from "@/lib/academy/progress-store";

/** Kein echtes externes Aenderungs-Event noetig (siehe points-badge.tsx fuer denselben
 * Zweck/dieselbe Begruendung) - stellt nur sicher, dass das Lesen des ggf. bereits
 * vorhandenen Ergebnisses aus localStorage keinen Hydration-Mismatch verursacht. */
function subscribe(): () => void {
  return () => {};
}

/** Kurze, wechselnde Bestätigungstexte bei richtiger Antwort (Erik 2026-08-20: "Tests
 * so gestalten, dass sie psychologisch einen positiven Effekt haben") - bewusst mehrere
 * statt eines einzigen fixen Texts, damit es sich bei mehreren Fragen nicht repetitiv
 * anfühlt. Rein zur Bestätigung, keine Bewertungs-/Handlungssprache. */
const CORRECT_PRAISE = ["Richtig!", "Genau so!", "Stark erkannt!", "Punktgenau!", "Sitzt!"];

/** Eigene, komponentenexterne Funktion fuer den Zufallsgriff (react-hooks/purity verbietet
 * `Math.random()` lexikalisch innerhalb der Komponente, auch in einem Event-Handler -
 * ausgelagert statt inline aufgerufen). */
function pickRandomPraiseIndex(): number {
  return Math.floor(Math.random() * CORRECT_PRAISE.length);
}

/** Ergebnis-Nachricht gestaffelt nach Trefferquote statt eines neutralen "X von Y" allein
 * (gleicher Wunsch wie oben) - immer positiv formuliert, auch bei einer niedrigeren Quote
 * (kein "schlecht abgeschnitten"-Unterton), passend zum bestehenden Prinzip aus
 * progress-store.ts ("Ausprobieren soll sich nie negativ anfühlen"). */
function resultHeadline(correctCount: number, total: number): string {
  const ratio = correctCount / total;
  if (ratio === 1) return "Perfekt gemeistert!";
  if (ratio >= 0.7) return "Stark gemacht!";
  if (ratio >= 0.4) return "Gut dabei!";
  return "Geschafft!";
}

/**
 * Akademie-Wissenstest (backlog.md -> [Punkt 34], Phase 1) - eine Frage nach der anderen,
 * sofortiges Feedback (richtig/falsch + Begründung) statt erst am Ende, macht den
 * Lerneffekt direkt sichtbar statt nur eine Endpunktzahl zu zeigen. Ergebnis wird beim
 * Abschluss in lib/academy/progress-store.ts (localStorage, Phase 1) gespeichert.
 */
export function AcademyQuiz({ slug, questions }: { slug: string; questions: QuizQuestion[] }) {
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [finished, setFinished] = useState(false);
  const previousResult = useSyncExternalStore<AcademyQuizResult | undefined>(
    subscribe,
    () => getAcademyQuizResult(slug),
    () => undefined,
  );

  const question = questions[index];
  const isLast = index === questions.length - 1;
  const wasCorrect = selected !== null && selected === question.correctIndex;
  // Bewusst im Event-Handler gewuerfelt (praiseIndex-State), nicht per Math.random() waehrend
  // des Renders - React 19s Purity-Regel (react-hooks/purity) verbietet impure Aufrufe im
  // Render-Pfad, auch innerhalb von useMemo. Ein State-Wert bleibt zudem automatisch stabil
  // ueber Re-Renders derselben Frage hinweg (z.B. durch previousResult-Aktualisierung).
  const [praiseIndex, setPraiseIndex] = useState(0);
  const praise = CORRECT_PRAISE[praiseIndex];

  function handleSelect(optionIndex: number) {
    if (selected !== null) return; // Antwort schon gegeben, keine Aenderung mehr
    setSelected(optionIndex);
    if (optionIndex === question.correctIndex) {
      setCorrectCount((c) => c + 1);
      setPraiseIndex(pickRandomPraiseIndex());
    }
  }

  function handleNext() {
    if (isLast) {
      const finalCorrect = correctCount;
      saveAcademyQuizResult(slug, finalCorrect, questions.length);
      setFinished(true);
      return;
    }
    setIndex((i) => i + 1);
    setSelected(null);
  }

  function handleRestart() {
    setIndex(0);
    setSelected(null);
    setCorrectCount(0);
    setFinished(false);
  }

  if (finished) {
    const isPerfect = correctCount === questions.length;
    return (
      <Card className="border-gold/30 bg-primary">
        <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
          <span
            className={cn(
              "flex h-14 w-14 items-center justify-center rounded-full bg-gold/15",
              isPerfect && "animate-in zoom-in duration-500",
            )}
          >
            {isPerfect ? (
              <Trophy className="h-7 w-7 text-gold" aria-hidden="true" />
            ) : (
              <Award className="h-7 w-7 text-gold" aria-hidden="true" />
            )}
          </span>
          <p className="font-serif text-2xl text-primary-foreground">{resultHeadline(correctCount, questions.length)}</p>
          <p className="text-sm text-primary-foreground/80">
            {correctCount} von {questions.length} richtig beantwortet
          </p>
          <p className="text-sm font-medium text-gold">+{correctCount * 10} Punkte gesammelt</p>
          <Button type="button" variant="outline" onClick={handleRestart} className="mt-2 gap-1.5">
            <RotateCcw className="h-3.5 w-3.5" />
            Test wiederholen
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 pt-6">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Frage {index + 1} von {questions.length}
          </span>
          {previousResult && (
            <span className="inline-flex items-center gap-1 text-gold-text">
              <Award className="h-3 w-3" aria-hidden="true" />
              Zuletzt: {previousResult.correctCount}/{previousResult.totalQuestions}
            </span>
          )}
        </div>
        <p className="font-medium text-ink">{question.question}</p>
        <div className="flex flex-col gap-2">
          {question.options.map((option, optionIndex) => {
            const isSelected = selected === optionIndex;
            const isCorrectOption = optionIndex === question.correctIndex;
            const showResult = selected !== null;
            return (
              <button
                key={option}
                type="button"
                onClick={() => handleSelect(optionIndex)}
                disabled={selected !== null}
                className={cn(
                  "flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                  !showResult && "border-hairline bg-card text-ink hover:bg-muted",
                  showResult && isCorrectOption && "border-positive bg-positive/10 text-ink",
                  showResult && isSelected && !isCorrectOption && "border-negative bg-negative/10 text-ink",
                  showResult && !isSelected && !isCorrectOption && "border-hairline bg-card text-muted-foreground",
                )}
              >
                <span>{option}</span>
                {showResult && isCorrectOption && <CheckCircle2 className="h-4 w-4 shrink-0 text-positive" />}
                {showResult && isSelected && !isCorrectOption && <XCircle className="h-4 w-4 shrink-0 text-negative" />}
              </button>
            );
          })}
        </div>
        {selected !== null && (
          <div className={cn("flex flex-col gap-3 rounded-lg p-3", wasCorrect ? "bg-positive/10" : "bg-muted/50")}>
            {wasCorrect && (
              <p className="animate-in fade-in slide-in-from-left-1 flex items-center gap-1.5 text-sm font-semibold text-positive duration-300">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                {praise}
              </p>
            )}
            <p className="text-xs text-ink-secondary">{question.explanation}</p>
            <Button type="button" onClick={handleNext} className="self-start">
              {isLast ? "Ergebnis anzeigen" : "Nächste Frage"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
